import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import Select from 'react-select'; 
import '../../style/CreatePet.css';
import dogBreeds from '../../data/dogBreeds';
import catBreeds from '../../data/catBreeds';
import rabbitBreeds from '../../data/rabbitBreeds';
import behaviorWithDogs from '../../data/behaviorWithDogs';
import behaviorWithCats from '../../data/behaviorWithCats';
import behaviorWithPeople from '../../data/behaviorWithPeople';
import colors from '../../data/colors';
import eyeColors from '../../data/eyeColors';
import recurrenceOptions from '../../data/recurrenceOptions'; 
import sexOptions from '../../data/sexOptions';
import sizeOptions from '../../data/sizeOptions';
import furAmountOptions from '../../data/furAmountOptions'; 
import furLengthOptions from '../../data/furLengthOptions'; 
import petSpecies from '../../data/petSpecies'; 
import { useTranslation } from 'react-i18next'; // Import translation hook
import i18n from '../../i18n'; // Import i18n instance



const CreateFirstPet = () => {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation(); 
    const [searchParams] = useSearchParams();
    const orgId = searchParams.get('orgId'); 
    const [step, setStep] = useState(1); // Track the current step
    const [petData, setPetData] = useState({
        name: '',
        petType: '', 
        description: '',
        general: {
            sex: '',
            birthDate: '',
            size: 'medium',
            fur: {
                hasFur: true, // Default to empty
                amount: '',
                length: '',
            },
            primaryColor: '',
            secondaryColors: [],
            eyeColors: [],
            isMixedBreed: false, // Default to false
            breeds: [], // Initialize as an empty array
        },
        behavior: {
            withPeople: [],
            withDogs: [],   // Initialize as an empty array
            withCats: [],
        },
        health: {
            weightHistory: [
                {
                    date: '', // Initialize as empty string
                    weightKg: null, // Initialize as null
                },
            ], // Initialize as an empty array
            conditions: [],
            hasChip: false,
            chipNumber: '',
            hasTattoo: false,
            castrated: false,
        },
    });
    const [images, setImages] = useState([]); // Separate state for images
    const [imagePreviews, setImagePreviews] = useState([]); // State for image previews
    const [loading, setLoading] = useState(false);

    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setPetData({ ...petData, [name]: value });
    };

    const handleGeneralChange = (e) => {
        const { name, value } = e.target;
    
        if (name === 'petType') {
            // Update petType directly at the top level
            setPetData({
                ...petData,
                [name]: value,
            });
        } else {
            // Update fields inside the general object
            setPetData({
                ...petData,
                general: { ...petData.general, [name]: value },
            });
        }
    };

    const getTranslatedLabel = (options, value) => {
        if (!value) {
            console.log('Value is empty, returning unknown.');
            return t('general.unknown'); // Return "unknown" if value is empty
        }
    
        console.log('Options:', options);
        console.log('Value:', value);
        const normalizedValue = value.trim().toLowerCase(); // Normalize the value
        const option = options.find((o) => o.value === normalizedValue);
        console.log('Normalized Value:', normalizedValue);
        console.log('Found Option:', option);
        console.log('Current Language:', i18n.language);
        return option ? option.label[i18n.language] : t('general.unknown'); // Use translation for "unknown"
    };

    const handleSecondaryColorsChange = (e) => {
        const { value, checked } = e.target; // Extract value and checked state from the event
        setPetData((prevState) => {
            const updatedSecondaryColors = checked
                ? [...prevState.general.secondaryColors, value] // Add the color if checked
                : prevState.general.secondaryColors.filter((color) => color !== value); // Remove the color if unchecked
            return {
                ...prevState,
                general: { ...prevState.general, secondaryColors: updatedSecondaryColors },
            };
        });
    };

    const handleFurChange = (e) => {
        const { name, value } = e.target;
        const booleanValue = value === 'true'; // Convert string to boolean
        setPetData((prevState) => ({
            ...prevState,
            general: {
                ...prevState.general,
                fur: {
                    ...prevState.general.fur,
                    [name]: name === 'hasFur' ? booleanValue : value, // Store boolean for hasFur
                    ...(name === 'hasFur' && !booleanValue
                        ? { amount: 'none', length: 'none' } // Clear `amount` and `length` if `hasFur` is false
                        : {}),
                },
            },
        }));
    };

    const handleEyeColorsChange = (e) => {
        const { value, checked } = e.target;
        setPetData((prevState) => {
            const updatedEyeColors = checked
                ? [...prevState.general.eyeColors, value]
                : prevState.general.eyeColors.filter((color) => color !== value);
            return {
                ...prevState,
                general: { ...prevState.general, eyeColors: updatedEyeColors },
            };
        });
    };

    const handleWithPeopleChange = (e) => {
        const { value, checked } = e.target;
        setPetData((prevState) => {
            const updatedWithPeople = checked
                ? [...prevState.behavior.withPeople, value]
                : prevState.behavior.withPeople.filter((behavior) => behavior !== value);
            return {
                ...prevState,
                behavior: { ...prevState.behavior, withPeople: updatedWithPeople },
            };
        });
    };

    const handleWithDogsChange = (e) => {
        const { value, checked } = e.target;
        setPetData((prevState) => {
            const updatedWithDogs = checked
                ? [...prevState.behavior.withDogs, value]
                : prevState.behavior.withDogs.filter((behavior) => behavior !== value);
            return {
                ...prevState,
                behavior: { ...prevState.behavior, withDogs: updatedWithDogs },
            };
        });
    };
    
    const handleWithCatsChange = (e) => {
        const { value, checked } = e.target;
        setPetData((prevState) => {
            const updatedWithCats = checked
                ? [...prevState.behavior.withCats, value]
                : prevState.behavior.withCats.filter((behavior) => behavior !== value);
            return {
                ...prevState,
                behavior: { ...prevState.behavior, withCats: updatedWithCats },
            };
        });
    };


    const handleBreedChange = (selectedOptions) => {
        setPetData((prevState) => ({
            ...prevState,
            general: {
                ...prevState.general,
                breeds: Array.isArray(selectedOptions) ? selectedOptions : [selectedOptions], // Ensure breeds is always an array
            },
        }));
    };

    const handleMixedBreedChange = (e) => {
        const { checked } = e.target;
        console.log('Mixed Breed Checkbox:', checked); 
        setPetData((prevState) => ({
            ...prevState,
            general: { ...prevState.general, isMixedBreed: checked, breeds: [] }, // Reset breeds if mixedBreed changes
        }));
    };



    const handleImageChange = (e) => {
        const files = Array.from(e.target.files);
        if (files.length > 1) {
            enqueueSnackbar(t('generalPet.oneImageOnly'), { variant: 'error' });
            return;
        }
        setImages(files); // Store the single selected file in state
        setImagePreviews(files.map((file) => URL.createObjectURL(file))); // Generate preview URLs
    };

    useEffect(() => {
        // Cleanup preview URLs to avoid memory leaks
        return () => {
            imagePreviews.forEach((url) => URL.revokeObjectURL(url));
        };
    }, [imagePreviews]);

    const handleSubmit = async (e) => {
        e.preventDefault(); // Prevent the default form submission behavior
        setLoading(true); // Set loading state to true
    
        try {
            // Validate required fields
            if (!petData.name.trim()) {
                enqueueSnackbar(t('generalPet.nameRequired'), { variant: 'error' });
                setLoading(false);
                return;
            }
    
            if (!petData.general.sex || petData.general.sex === '') {
                enqueueSnackbar(t('generalPet.genderRequired'), { variant: 'error' });
                setLoading(false);
                return;
            }
    
            if (!petData.general.birthDate) {
                enqueueSnackbar(t('generalPet.birthDateRequired'), { variant: 'error' });
                setLoading(false);
                return;
            }
    
            if (!petData.petType || petData.petType === '') {
                enqueueSnackbar(t('generalPet.typeRequired'), { variant: 'error' });
                setLoading(false);
                return;
            }
    
            if (!Array.isArray(petData.general.breeds)) {
                enqueueSnackbar(t('generalPet.breedRequired'), { variant: 'error' });
                setLoading(false);
                return;
            }
    
            // Retrieve the authentication token
            const storedUser = JSON.parse(localStorage.getItem('user'));
            const token = storedUser?.token;
    
            if (!token) {
                throw new Error(t('general.noAuthToken'));
            }
    
            // Transform data before sending to the backend
            const transformedPetData = {
                ...petData,
                general: {
                    ...petData.general,
                    breeds: petData.general.breeds.map((breed) => breed.value), // Extract `value` from each breed object
                    fur: {
                        ...petData.general.fur,
                        amount: petData.general.fur.hasFur === 'false' ? 'none' : petData.general.fur.amount, // Set `amount` to 'none' if `hasFur` is false
                        length: petData.general.fur.hasFur === 'false' ? 'none' : petData.general.fur.length, // Ensure valid enum value
                    },
                },
                health: {
                    ...petData.health,
                    weightHistory: petData.health?.weightHistory?.filter(
                        (entry) => entry.date && entry.weightKg // Include only valid entries
                    ),
                },
            };
    
            console.log('Transformed Pet Data:', transformedPetData);
    
            // Create a FormData object to handle file uploads
            const formData = new FormData();
            formData.append('name', transformedPetData.name);
            formData.append('petType', transformedPetData.petType);
            formData.append('description', transformedPetData.description);
            formData.append('general', JSON.stringify(transformedPetData.general)); // Send general as JSON string
            formData.append('behavior', JSON.stringify(transformedPetData.behavior)); // Send behavior as JSON string
            formData.append('health', JSON.stringify(transformedPetData.health)); // Send health as JSON string
            formData.append('organizationId', orgId);
            if (images.length === 1) {
                formData.append('file', images[0]); // Use 'file' as the field name for single image upload
                formData.append('imageTag', 'profile'); // Add the 'profile' tag
            }
    
            // Send the form data to the backend
            const response = await axios.post(`${baseUrl}/api/pets/create-with-default-org`, formData, {
                headers: {
                    Authorization: `Bearer ${token}`, // Include the token in the Authorization header
                    'Content-Type': 'multipart/form-data', // Set the content type for file uploads
                },
            });
    
            console.log('Response:', response.data);
    
            setLoading(false); // Set loading state to false
            enqueueSnackbar(t('createPetPage.successMessage'), { variant: 'success' }); // Show success message
            navigate(`/`); // Redirect to the home page
        } catch (error) {
            setLoading(false); // Set loading state to false
            console.error('Error adding pet:', error.response || error.message);
            enqueueSnackbar(t('createPetPage.errorMessage'), { variant: 'error' }); // Show error message
        }
    };

    const renderStep = () => {
        switch (step) {
            case 1:
                console.log('Selected petType:', petData);
                console.log('Breed options:', petData.petType === 'dog' ? dogBreeds : catBreeds);
                return (
                    <div className="form-step space-y-6">
                        <h3 className="text-dafauna-darkText text-xl font-bold text-center">{t('generalPet.step1Title')}</h3>
                        <div className="form-group">
                            <label htmlFor="name" className="block text-dafauna-darkText font-medium mb-2">{t('generalPet.petName')}</label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                value={petData.name}
                                onChange={handleInputChange}
                                required
                                className="form-input w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="petType" className="block text-dafauna-darkText font-medium mb-2">{t('generalPet.petType')}</label>
                            <select
                                id="petType"
                                name="petType"
                                value={petData.petType}
                                onChange={handleGeneralChange}
                                required
                                className="form-select w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                            >
                                <option value="">{t('general.selectOption')}</option>
                                {petSpecies.map((species) => (
                                    <option key={species.value} value={species.value}>
                                        {getTranslatedLabel(petSpecies, species.value)}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="form-group">
                            <label htmlFor="sex" className="block text-dafauna-darkText font-medium mb-2">{t('generalPet.gender')}</label>
                            <select
                                id="sex"
                                name="sex"
                                value={petData.general.sex}
                                onChange={handleGeneralChange}
                                className="form-select w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                            >
                                <option value="">{t('general.selectOption')}</option>
                                {sexOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {getTranslatedLabel(sexOptions, option.value)}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="form-group">
                            <label htmlFor="birthDate" className="block text-dafauna-darkText font-medium mb-2">{t('generalPet.birthDate')}</label>
                            <input
                                type="date"
                                id="birthDate"
                                name="birthDate"
                                value={petData.general.birthDate}
                                onChange={handleGeneralChange}
                                required
                                className="form-input w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="description" className="block text-dafauna-darkText font-medium mb-2">Moto</label>
                            <textarea
                                id="description"
                                name="description"
                                value={petData.description}
                                onChange={handleInputChange}
                                required
                                className="form-textarea w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="images" className="block text-dafauna-darkText font-medium mb-2">Pet Images</label>
                            <input
                                type="file"
                                id="images"
                                name="images"
                                accept="image/*"
                                multiple
                                onChange={handleImageChange}
                                className="form-input w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                            />
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '10px' }}>
                            {imagePreviews.map((src, index) => (
                                <img
                                    key={index}
                                    src={src}
                                    alt={`Preview ${index + 1}`}
                                    style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '8px' }}
                                />
                            ))}
                        </div>
                        <button
                            onClick={() => {
                                if (!petData.name.trim()) {
                                    enqueueSnackbar(t('generalPet.nameRequired'), { variant: 'error' });
                                    return;
                                }
                                if (!petData.petType || petData.petType === '') {
                                    enqueueSnackbar(t('generalPet.typeRequired'), { variant: 'error' });
                                    return;
                                }
                                if (!petData.general.sex || petData.general.sex === '') {
                                    enqueueSnackbar(t('generalPet.genderRequired'), { variant: 'error' });
                                    return;
                                }
                                if (!petData.general.birthDate) {
                                    enqueueSnackbar(t('generalPet.birthDateRequired'), { variant: 'error' });
                                    return;
                                }
                                setStep(2);
                            }}
                            className="btn next-btn w-full bg-dafauna-primary text-white rounded-md px-4 py-2 hover:bg-dafauna-primaryLight"
                        >
                            {t('general.next')}
                        </button>
                    </div>
                );
            case 2:
                return (
                    <div className="form-step space-y-6">
                        <h3 className="text-dafauna-darkText text-xl font-bold text-center">{t('generalPet.step2Title')}</h3>
                        <div className="form-group-mixed-breed flex items-center gap-4">
                            <label htmlFor="isMixedBreed" className="text-dafauna-darkText font-medium">{t('generalPet.isMixedBreed')}</label>
                            <input
                                type="checkbox"
                                id="isMixedBreed"
                                name="isMixedBreed"
                                checked={petData.general.isMixedBreed}
                                onChange={handleMixedBreedChange}
                                className="form-checkbox"
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="breeds" className="block text-dafauna-darkText font-medium mb-2">{t('generalPet.breedSelection')}</label>
                            <Select
                                id="breeds"
                                name="breeds"
                                isMulti={petData.general.isMixedBreed}
                                options={
                                    petData.petType === 'dog'
                                        ? dogBreeds.map((breed) => ({
                                            value: breed.value,
                                            label: getTranslatedLabel(dogBreeds, breed.value),
                                        }))
                                        : petData.petType === 'cat'
                                        ? catBreeds.map((breed) => ({
                                            value: breed.value,
                                            label: getTranslatedLabel(catBreeds, breed.value),
                                        }))
                                        : petData.petType === 'rabbit'
                                        ? rabbitBreeds.map((breed) => ({
                                            value: breed.value,
                                            label: getTranslatedLabel(rabbitBreeds, breed.value),
                                        }))
                                        : []
                                }
                                value={petData.general.breeds}
                                onChange={handleBreedChange}
                                placeholder={
                                    petData.general.isMixedBreed
                                        ? t('generalPet.selectBreedsOrUnknown')
                                        : t('generalPet.selectBreedOrUnknown')
                                }
                                className="form-select w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="size" className="block text-dafauna-darkText font-medium mb-2">{t('generalPet.size')}</label>
                            <select
                                id="size"
                                name="size"
                                value={petData.general.size}
                                onChange={handleGeneralChange}
                                className="form-select w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                            >
                                {sizeOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {getTranslatedLabel(sizeOptions, option.value)}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="form-group">
                            <label htmlFor="hasFur" className="block text-dafauna-darkText font-medium mb-2">{t('generalPet.hasFur')}</label>
                            <select
                                id="hasFur"
                                name="hasFur"
                                value={petData.general.fur.hasFur}
                                onChange={handleFurChange}
                                className="form-select w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                            >
                                <option value={true}>{t('general.yes')}</option>
                                <option value={false}>{t('general.no')}</option>
                            </select>
                        </div>
                        {petData.general.fur.hasFur && (
                            <>
                                <div className="form-group">
                                    <label htmlFor="amount" className="block text-dafauna-darkText font-medium mb-2">{t('generalPet.furAmount')}</label>
                                    <select
                                        id="amount"
                                        name="amount"
                                        value={petData.general.fur.amount}
                                        onChange={handleFurChange}
                                        className="form-select w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                                    >
                                        <option value="">{t('generalPet.selectAmount')}</option>
                                        {furAmountOptions.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {getTranslatedLabel(furAmountOptions, option.value)}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="length" className="block text-dafauna-darkText font-medium mb-2">{t('generalPet.furLength')}</label>
                                    <select
                                        id="length"
                                        name="length"
                                        value={petData.general.fur.length}
                                        onChange={handleFurChange}
                                        className="form-select w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                                    >
                                        <option value="">{t('generalPet.selectLength')}</option>
                                        {furLengthOptions.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {getTranslatedLabel(furLengthOptions, option.value)}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </>
                        )}
                        <div className="form-group">
                            <label htmlFor="primaryColor" className="block text-dafauna-darkText font-medium mb-2">{t('generalPet.primaryColor')}</label>
                            <select
                                id="primaryColor"
                                name="primaryColor"
                                value={petData.general.primaryColor}
                                onChange={handleGeneralChange}
                                className="form-select w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                            >
                                <option value="">{t('generalPet.selectColor')}</option>
                                {colors.map((color) => (
                                    <option key={color.value} value={color.value}>
                                        {getTranslatedLabel(colors, color.value)}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="form-group">
                            <label className="block text-dafauna-darkText font-medium mb-2">{t('generalPet.secondaryColors')}</label>
                            <div className="secondary-colors flex flex-wrap gap-4">
                                {colors.map((color) => (
                                    <div key={color.value} className="color-box">
                                        <input
                                            type="checkbox"
                                            id={`color-${color.value}`}
                                            value={color.value}
                                            checked={petData.general.secondaryColors.includes(color.value)}
                                            onChange={handleSecondaryColorsChange}
                                            className="hidden"
                                        />
                                        <label
                                            htmlFor={`color-${color.value}`}
                                            className="block w-24 h-10 border border-gray-300 rounded-md text-center leading-10 cursor-pointer hover:bg-dafauna-primaryLight"
                                        >
                                            {getTranslatedLabel(colors, color.value)}
                                        </label>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="form-group">
                            <label className="block text-dafauna-darkText font-medium mb-2">{t('generalPet.eyeColors')}</label>
                            <div className="eye-colors flex flex-wrap gap-4">
                                {eyeColors.map((color) => (
                                    <div key={color.value} className="color-box">
                                        <input
                                            type="checkbox"
                                            id={`eye-color-${color.value}`}
                                            value={color.value}
                                            checked={petData.general.eyeColors.includes(color.value)}
                                            onChange={handleEyeColorsChange}
                                            className="hidden"
                                        />
                                        <label
                                            htmlFor={`eye-color-${color.value}`}
                                            className="block w-24 h-10 border border-gray-300 rounded-md text-center leading-10 cursor-pointer hover:bg-dafauna-primaryLight"
                                        >
                                            {getTranslatedLabel(eyeColors, color.value)}
                                        </label>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="nav-add-pet flex justify-between mt-6">
                            <button
                                onClick={() => setStep(1)}
                                className="btn back-btn bg-gray-400 text-white rounded-md px-4 py-2 hover:bg-gray-500"
                            >
                                {t('general.back')}
                            </button>
                            <button
                                onClick={() => {
                                    if (!petData.general.breeds || petData.general.breeds.length === 0) {
                                        enqueueSnackbar(t('generalPet.breedRequired'), { variant: 'error' });
                                        return;
                                    }
                                    if (petData.general.fur.hasFur && (!petData.general.fur.amount || !petData.general.fur.length)) {
                                        enqueueSnackbar(t('generalPet.furRequired'), { variant: 'error' });
                                        return;
                                    }
                                    setStep(3);
                                }}
                                className="btn next-btn bg-dafauna-primary text-white rounded-md px-4 py-2 hover:bg-dafauna-primaryLight"
                            >
                                {t('general.next')}
                            </button>
                        </div>
                    </div>
                );
            case 3:
                return (
                    <div className="form-step space-y-6">
                        <h3 className="text-dafauna-darkText text-xl font-bold text-center">{t('generalPet.step3Title')}</h3>
                        <div className="form-group">
                            <label className="block text-dafauna-darkText font-medium mb-2">{t('generalPet.behaviorWithPeople')}</label>
                            <div className="behavior-with-people flex flex-wrap gap-4">
                                {behaviorWithPeople.map((behavior) => (
                                    <div key={behavior.value} className="behavior-box">
                                        <input
                                            type="checkbox"
                                            id={`behavior-people-${behavior.value}`}
                                            value={behavior.value}
                                            checked={petData.behavior.withPeople.includes(behavior.value)}
                                            onChange={handleWithPeopleChange}
                                            className="hidden"
                                        />
                                        <label
                                            htmlFor={`behavior-people-${behavior.value}`}
                                            className="block w-24 h-10 border border-gray-300 rounded-md text-center leading-10 cursor-pointer hover:bg-dafauna-primaryLight"
                                        >
                                            {getTranslatedLabel(behaviorWithPeople, behavior.value)}
                                        </label>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="form-group">
                            <label className="block text-dafauna-darkText font-medium mb-2">{t('generalPet.behaviorWithDogs')}</label>
                            <div className="behavior-with-dogs flex flex-wrap gap-4">
                                {behaviorWithDogs.map((behavior) => (
                                    <div key={behavior.value} className="behavior-box">
                                        <input
                                            type="checkbox"
                                            id={`behavior-dogs-${behavior.value}`}
                                            value={behavior.value}
                                            checked={petData.behavior.withDogs.includes(behavior.value)}
                                            onChange={handleWithDogsChange}
                                            className="hidden"
                                        />
                                        <label
                                            htmlFor={`behavior-dogs-${behavior.value}`}
                                            className="block w-24 h-10 border border-gray-300 rounded-md text-center leading-10 cursor-pointer hover:bg-dafauna-primaryLight"
                                        >
                                            {getTranslatedLabel(behaviorWithDogs, behavior.value)}
                                        </label>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="form-group">
                            <label className="block text-dafauna-darkText font-medium mb-2">{t('generalPet.behaviorWithCats')}</label>
                            <div className="behavior-with-cats flex flex-wrap gap-4">
                                {behaviorWithCats.map((behavior) => (
                                    <div key={behavior.value} className="behavior-box">
                                        <input
                                            type="checkbox"
                                            id={`behavior-cats-${behavior.value}`}
                                            value={behavior.value}
                                            checked={petData.behavior.withCats.includes(behavior.value)}
                                            onChange={handleWithCatsChange}
                                            className="hidden"
                                        />
                                        <label
                                            htmlFor={`behavior-cats-${behavior.value}`}
                                            className="block w-24 h-10 border border-gray-300 rounded-md text-center leading-10 cursor-pointer hover:bg-dafauna-primaryLight"
                                        >
                                            {getTranslatedLabel(behaviorWithCats, behavior.value)}
                                        </label>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="nav-add-pet flex justify-between mt-6">
                            <button
                                onClick={() => setStep(2)}
                                className="btn back-btn bg-gray-400 text-white rounded-md px-4 py-2 hover:bg-gray-500"
                            >
                                {t('general.back')}
                            </button>
                            <button
                                onClick={() => setStep(4)}
                                className="btn next-btn bg-dafauna-primary text-white rounded-md px-4 py-2 hover:bg-dafauna-primaryLight"
                            >
                                {t('general.next')}
                            </button>
                        </div>
                    </div>
                );
            case 4:
                console.log('Selected petType:', petData);
                return (
                    <div className="form-step">
                        <h3 className="text-dafauna-darkText text-xl font-bold text-center mb-6">{t('generalPet.step4Title')}</h3>
                        <div className="form-group-weight flex items-center gap-4">
                            <label htmlFor="weight" className="text-dafauna-darkText font-medium">{t('generalPet.weight')} (kg)</label>
                            <input
                                type="number"
                                id="weight"
                                name="weight"
                                value={petData.health?.weightHistory?.[0]?.weightKg || ''}
                                onChange={(e) => {
                                    const { value } = e.target;
                                    const today = new Date().toISOString().split('T')[0];
                                    const weightKg = parseFloat(value);
                                    if (!weightKg) return;
                                    setPetData((prevState) => ({
                                        ...prevState,
                                        health: {
                                            ...prevState.health,
                                            weightHistory: [
                                                { date: today, weightKg },
                                                ...prevState.health.weightHistory.filter((entry) => entry.date && entry.weightKg),
                                            ],
                                        },
                                    }));
                                }}
                                className="form-input w-24 border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                            />
                        </div>
                        <div className="form-group-castrated flex items-center gap-4">
                            <label htmlFor="castrated" className="text-dafauna-darkText font-medium">{t('generalPet.isCastrated')}</label>
                            <input
                                type="checkbox"
                                id="castrated"
                                name="castrated"
                                checked={petData.health?.castrated || false}
                                onChange={(e) => {
                                    const { checked } = e.target;
                                    setPetData((prevState) => ({
                                        ...prevState,
                                        health: { ...prevState.health, castrated: checked },
                                    }));
                                }}
                                className="form-checkbox"
                            />
                        </div>
                        <div className="form-group-chip flex items-center gap-4">
                            <label htmlFor="hasChip" className="text-dafauna-darkText font-medium">{t('generalPet.hasChip')}</label>
                            <input
                                type="checkbox"
                                id="hasChip"
                                name="hasChip"
                                checked={petData.health?.hasChip || false}
                                onChange={(e) => {
                                    const { checked } = e.target;
                                    setPetData((prevState) => ({
                                        ...prevState,
                                        health: { ...prevState.health, hasChip: checked, chipNumber: checked ? prevState.health?.chipNumber || '' : null },
                                    }));
                                }}
                                className="form-checkbox"
                            />
                        </div>
                        {petData.health?.hasChip && (
                            <div className="form-group">
                                <label htmlFor="chipNumber" className="block text-dafauna-darkText font-medium mb-2">{t('generalPet.chipNumber')}</label>
                                <input
                                    type="text"
                                    id="chipNumber"
                                    name="chipNumber"
                                    value={petData.health?.chipNumber || ''}
                                    onChange={(e) => {
                                        const { value } = e.target;
                                        setPetData((prevState) => ({
                                            ...prevState,
                                            health: { ...prevState.health, chipNumber: value },
                                        }));
                                    }}
                                    className="form-input w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                                />
                            </div>
                        )}
                        <div className="form-group-tattoo flex items-center gap-4">
                            <label htmlFor="hasTattoo" className="text-dafauna-darkText font-medium">{t('generalPet.hasTattoo')}</label>
                            <input
                                type="checkbox"
                                id="hasTattoo"
                                name="hasTattoo"
                                checked={petData.health?.hasTattoo || false}
                                onChange={(e) => {
                                    const { checked } = e.target;
                                    setPetData((prevState) => ({
                                        ...prevState,
                                        health: { ...prevState.health, hasTattoo: checked },
                                    }));
                                }}
                                className="form-checkbox"
                            />
                        </div>
                
                        <div className="form-group-disability flex items-center gap-4">
                            <label htmlFor="hasDisability" className="text-dafauna-darkText font-medium">{t('condition.hasDisability')}</label>
                            <input
                                type="checkbox"
                                id="hasDisability"
                                name="hasDisability"
                                checked={petData.health?.conditions?.some((condition) => condition.type === 'disability') || false}
                                onChange={(e) => {
                                    const { checked } = e.target;
                                    setPetData((prevState) => ({
                                        ...prevState,
                                        health: {
                                            ...prevState.health,
                                            conditions: checked
                                                ? [
                                                    ...(prevState.health.conditions || []),
                                                    { type: 'disability', name: '', diagnosisDate: '', notes: '', treatments: [] },
                                                ]
                                                : prevState.health.conditions.filter((condition) => condition.type !== 'disability'),
                                        },
                                    }));
                                }}
                                className="form-checkbox"
                            />
                        </div>
                        {petData.health?.conditions?.some((condition) => condition.type === 'disability') && (
                            <>
                                <div className="form-group">
                                    <label htmlFor="disabilityName" className="block text-dafauna-darkText font-medium mb-2">{t('condition.disabilityName')}</label>
                                    <input
                                        type="text"
                                        id="disabilityName"
                                        name="disabilityName"
                                        value={
                                            petData.health.conditions.find((condition) => condition.type === 'disability')?.name || ''
                                        }
                                        onChange={(e) => {
                                            const { value } = e.target;
                                            setPetData((prevState) => ({
                                                ...prevState,
                                                health: {
                                                    ...prevState.health,
                                                    conditions: prevState.health.conditions.map((condition) =>
                                                        condition.type === 'disability' ? { ...condition, name: value } : condition
                                                    ),
                                                },
                                            }));
                                        }}
                                        className="form-input w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="diagnosisDate" className="block text-dafauna-darkText font-medium mb-2">{t('condition.diagnosisDate')}</label>
                                    <input
                                        type="date"
                                        id="diagnosisDate"
                                        name="diagnosisDate"
                                        value={
                                            petData.health.conditions.find((condition) => condition.type === 'disability')
                                                ?.diagnosisDate || ''
                                        }
                                        onChange={(e) => {
                                            const { value } = e.target;
                                            setPetData((prevState) => ({
                                                ...prevState,
                                                health: {
                                                    ...prevState.health,
                                                    conditions: prevState.health.conditions.map((condition) =>
                                                        condition.type === 'disability' ? { ...condition, diagnosisDate: value } : condition
                                                    ),
                                                },
                                            }));
                                        }}
                                        className="form-input w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="notes" className="block text-dafauna-darkText font-medium mb-2">{t('condition.notes')}</label>
                                    <textarea
                                        id="notes"
                                        name="notes"
                                        value={
                                            petData.health.conditions.find((condition) => condition.type === 'disability')?.notes || ''
                                        }
                                        onChange={(e) => {
                                            const { value } = e.target;
                                            setPetData((prevState) => ({
                                                ...prevState,
                                                health: {
                                                    ...prevState.health,
                                                    conditions: prevState.health.conditions.map((condition) =>
                                                        condition.type === 'disability' ? { ...condition, notes: value } : condition
                                                    ),
                                                },
                                            }));
                                        }}
                                        className="form-textarea w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                                    />
                                </div>
                                <div className="form-group-hasTreatment flex items-center gap-4">
                                    <label htmlFor="hasTreatment" className="text-dafauna-darkText font-medium">{t('condition.disabilityTreatment')}</label>
                                    <input
                                        type="checkbox"
                                        id="hasTreatment"
                                        name="hasTreatment"
                                        checked={
                                            petData.health.conditions.find((condition) => condition.type === 'disability')?.treatments
                                                ?.length > 0 || false
                                        }
                                        onChange={(e) => {
                                            const { checked } = e.target;
                                            setPetData((prevState) => ({
                                                ...prevState,
                                                health: {
                                                    ...prevState.health,
                                                    conditions: prevState.health.conditions.map((condition) =>
                                                        condition.type === 'disability'
                                                            ? {
                                                                ...condition,
                                                                treatments: checked
                                                                    ? [{ name: '', lastGivenDate: '', recurrence: '', endDate: '' }]
                                                                    : [],
                                                            }
                                                            : condition
                                                    ),
                                                },
                                            }));
                                        }}
                                        className="form-checkbox"
                                    />
                                </div>
                                {petData.health.conditions.find((condition) => condition.type === 'disability')?.treatments?.length > 0 && (
                                    <>
                                        <div className="form-group">
                                            <label htmlFor="treatmentName" className="block text-dafauna-darkText font-medium mb-2">{t('condition.treatmentName')}</label>
                                            <input
                                                type="text"
                                                id="treatmentName"
                                                name="treatmentName"
                                                value={
                                                    petData.health.conditions.find((condition) => condition.type === 'disability')
                                                        ?.treatments[0]?.name || ''
                                                }
                                                onChange={(e) => {
                                                    const { value } = e.target;
                                                    setPetData((prevState) => ({
                                                        ...prevState,
                                                        health: {
                                                            ...prevState.health,
                                                            conditions: prevState.health.conditions.map((condition) =>
                                                                condition.type === 'disability'
                                                                    ? {
                                                                        ...condition,
                                                                        treatments: condition.treatments.map((treatment, index) =>
                                                                            index === 0 ? { ...treatment, name: value } : treatment
                                                                        ),
                                                                    }
                                                                    : condition
                                                            ),
                                                        },
                                                    }));
                                                }}
                                                className="form-input w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="lastGivenDate" className="block text-dafauna-darkText font-medium mb-2">{t('condition.lastGivenDate')}</label>
                                            <input
                                                type="date"
                                                id="lastGivenDate"
                                                name="lastGivenDate"
                                                value={
                                                    petData.health.conditions.find((condition) => condition.type === 'disability')
                                                        ?.treatments[0]?.lastGivenDate || ''
                                                }
                                                onChange={(e) => {
                                                    const { value } = e.target;
                                                    setPetData((prevState) => ({
                                                        ...prevState,
                                                        health: {
                                                            ...prevState.health,
                                                            conditions: prevState.health.conditions.map((condition) =>
                                                                condition.type === 'disability'
                                                                    ? {
                                                                        ...condition,
                                                                        treatments: condition.treatments.map((treatment, index) =>
                                                                            index === 0 ? { ...treatment, lastGivenDate: value } : treatment
                                                                        ),
                                                                    }
                                                                    : condition
                                                            ),
                                                        },
                                                    }));
                                                }}
                                                className="form-input w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="recurrence" className="block text-dafauna-darkText font-medium mb-2">{t('condition.recurrence')}</label>
                                            <select
                                                id="recurrence"
                                                name="recurrence"
                                                value={
                                                    petData.health.conditions.find((condition) => condition.type === 'disability')
                                                        ?.treatments[0]?.recurrence || ''
                                                }
                                                onChange={(e) => {
                                                    const { value } = e.target;
                                                    setPetData((prevState) => ({
                                                        ...prevState,
                                                        health: {
                                                            ...prevState.health,
                                                            conditions: prevState.health.conditions.map((condition) =>
                                                                condition.type === 'disability'
                                                                    ? {
                                                                        ...condition,
                                                                        treatments: condition.treatments.map((treatment, index) =>
                                                                            index === 0 ? { ...treatment, recurrence: value } : treatment
                                                                        ),
                                                                    }
                                                                    : condition
                                                            ),
                                                        },
                                                    }));
                                                }}
                                                className="form-select w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                                            >
                                                <option value="">{t('general.selectOption')}</option>
                                                {recurrenceOptions.map((option) => (
                                                    <option key={option.value} value={option.value}>
                                                        {getTranslatedLabel(recurrenceOptions, option.value)}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="endDate" className="block text-dafauna-darkText font-medium mb-2">{t('condition.endDate')}</label>
                                            <input
                                                type="date"
                                                id="endDate"
                                                name="endDate"
                                                value={
                                                    petData.health.conditions.find((condition) => condition.type === 'disability')
                                                        ?.treatments[0]?.endDate || ''
                                                }
                                                onChange={(e) => {
                                                    const { value } = e.target;
                                                    setPetData((prevState) => ({
                                                        ...prevState,
                                                        health: {
                                                            ...prevState.health,
                                                            conditions: prevState.health.conditions.map((condition) =>
                                                                condition.type === 'disability'
                                                                    ? {
                                                                        ...condition,
                                                                        treatments: condition.treatments.map((treatment, index) =>
                                                                            index === 0 ? { ...treatment, endDate: value } : treatment
                                                                        ),
                                                                    }
                                                                    : condition
                                                            ),
                                                        },
                                                    }));
                                                }}
                                                className="form-input w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                                            />
                                        </div>
                                    </>
                                )}
                            </>
                        )}

                        <div className="form-group-allergy flex items-center gap-4">
                            <label htmlFor="hasAllergy" className="text-dafauna-darkText font-medium">{t('condition.hasAllergy')}</label>
                            <input
                                type="checkbox"
                                id="hasAllergy"
                                name="hasAllergy"
                                checked={petData.health?.conditions?.some((condition) => condition.type === 'allergy') || false}
                                onChange={(e) => {
                                    const { checked } = e.target;
                                    setPetData((prevState) => ({
                                        ...prevState,
                                        health: {
                                            ...prevState.health,
                                            conditions: checked
                                                ? [
                                                    ...(prevState.health.conditions || []),
                                                    { type: 'allergy', name: '', diagnosisDate: '', notes: '', treatments: [] },
                                                ]
                                                : prevState.health.conditions.filter((condition) => condition.type !== 'allergy'),
                                        },
                                    }));
                                }}
                                className="form-checkbox"
                            />
                        </div>
                        {petData.health?.conditions?.some((condition) => condition.type === 'allergy') && (
                            <>
                                <div className="form-group">
                                    <label htmlFor="allergyName" className="block text-dafauna-darkText font-medium mb-2">{t('condition.allergyName')}</label>
                                    <input
                                        type="text"
                                        id="allergyName"
                                        name="allergyName"
                                        value={
                                            petData.health.conditions.find((condition) => condition.type === 'allergy')?.name || ''
                                        }
                                        onChange={(e) => {
                                            const { value } = e.target;
                                            setPetData((prevState) => ({
                                                ...prevState,
                                                health: {
                                                    ...prevState.health,
                                                    conditions: prevState.health.conditions.map((condition) =>
                                                        condition.type === 'allergy' ? { ...condition, name: value } : condition
                                                    ),
                                                },
                                            }));
                                        }}
                                        className="form-input w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="diagnosisDate" className="block text-dafauna-darkText font-medium mb-2">{t('condition.diagnosisDate')}</label>
                                    <input
                                        type="date"
                                        id="diagnosisDate"
                                        name="diagnosisDate"
                                        value={
                                            petData.health.conditions.find((condition) => condition.type === 'allergy')
                                                ?.diagnosisDate || ''
                                        }
                                        onChange={(e) => {
                                            const { value } = e.target;
                                            setPetData((prevState) => ({
                                                ...prevState,
                                                health: {
                                                    ...prevState.health,
                                                    conditions: prevState.health.conditions.map((condition) =>
                                                        condition.type === 'allergy' ? { ...condition, diagnosisDate: value } : condition
                                                    ),
                                                },
                                            }));
                                        }}
                                        className="form-input w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="notes" className="block text-dafauna-darkText font-medium mb-2">{t('condition.notes')}</label>
                                    <textarea
                                        id="notes"
                                        name="notes"
                                        value={
                                            petData.health.conditions.find((condition) => condition.type === 'allergy')?.notes || ''
                                        }
                                        onChange={(e) => {
                                            const { value } = e.target;
                                            setPetData((prevState) => ({
                                                ...prevState,
                                                health: {
                                                    ...prevState.health,
                                                    conditions: prevState.health.conditions.map((condition) =>
                                                        condition.type === 'allergy' ? { ...condition, notes: value } : condition
                                                    ),
                                                },
                                            }));
                                        }}
                                        className="form-textarea w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                                    />
                                </div>
                                <div className="form-group-hasTreatment flex items-center gap-4">
                                    <label htmlFor="hasTreatment" className="text-dafauna-darkText font-medium">{t('condition.allergyTreatment')}</label>
                                    <input
                                        type="checkbox"
                                        id="hasTreatment"
                                        name="hasTreatment"
                                        checked={
                                            petData.health.conditions.find((condition) => condition.type === 'allergy')?.treatments
                                                ?.length > 0 || false
                                        }
                                        onChange={(e) => {
                                            const { checked } = e.target;
                                            setPetData((prevState) => ({
                                                ...prevState,
                                                health: {
                                                    ...prevState.health,
                                                    conditions: prevState.health.conditions.map((condition) =>
                                                        condition.type === 'allergy'
                                                            ? {
                                                                ...condition,
                                                                treatments: checked
                                                                    ? [{ name: '', lastGivenDate: '', recurrence: '', endDate: '' }]
                                                                    : [],
                                                            }
                                                            : condition
                                                    ),
                                                },
                                            }));
                                        }}
                                        className="form-checkbox"
                                    />
                                </div>
                                {petData.health.conditions.find((condition) => condition.type === 'allergy')?.treatments?.length > 0 && (
                                    <>
                                        <div className="form-group">
                                            <label htmlFor="treatmentName" className="block text-dafauna-darkText font-medium mb-2">{t('condition.treatmentName')}</label>
                                            <input
                                                type="text"
                                                id="treatmentName"
                                                name="treatmentName"
                                                value={
                                                    petData.health.conditions.find((condition) => condition.type === 'allergy')
                                                        ?.treatments[0]?.name || ''
                                                }
                                                onChange={(e) => {
                                                    const { value } = e.target;
                                                    setPetData((prevState) => ({
                                                        ...prevState,
                                                        health: {
                                                            ...prevState.health,
                                                            conditions: prevState.health.conditions.map((condition) =>
                                                                condition.type === 'allergy'
                                                                    ? {
                                                                        ...condition,
                                                                        treatments: condition.treatments.map((treatment, index) =>
                                                                            index === 0 ? { ...treatment, name: value } : treatment
                                                                        ),
                                                                    }
                                                                    : condition
                                                            ),
                                                        },
                                                    }));
                                                }}
                                                className="form-input w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="lastGivenDate" className="block text-dafauna-darkText font-medium mb-2">{t('condition.lastGivenDate')}</label>
                                            <input
                                                type="date"
                                                id="lastGivenDate"
                                                name="lastGivenDate"
                                                value={
                                                    petData.health.conditions.find((condition) => condition.type === 'allergy')
                                                        ?.treatments[0]?.lastGivenDate || ''
                                                }
                                                onChange={(e) => {
                                                    const { value } = e.target;
                                                    setPetData((prevState) => ({
                                                        ...prevState,
                                                        health: {
                                                            ...prevState.health,
                                                            conditions: prevState.health.conditions.map((condition) =>
                                                                condition.type === 'allergy'
                                                                    ? {
                                                                        ...condition,
                                                                        treatments: condition.treatments.map((treatment, index) =>
                                                                            index === 0 ? { ...treatment, lastGivenDate: value } : treatment
                                                                        ),
                                                                    }
                                                                    : condition
                                                            ),
                                                        },
                                                    }));
                                                }}
                                                className="form-input w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="recurrence" className="block text-dafauna-darkText font-medium mb-2">{t('condition.recurrence')}</label>
                                            <select
                                                id="recurrence"
                                                name="recurrence"
                                                value={
                                                    petData.health.conditions.find((condition) => condition.type === 'allergy')
                                                        ?.treatments[0]?.recurrence || ''
                                                }
                                                onChange={(e) => {
                                                    const { value } = e.target;
                                                    setPetData((prevState) => ({
                                                        ...prevState,
                                                        health: {
                                                            ...prevState.health,
                                                            conditions: prevState.health.conditions.map((condition) =>
                                                                condition.type === 'allergy'
                                                                    ? {
                                                                        ...condition,
                                                                        treatments: condition.treatments.map((treatment, index) =>
                                                                            index === 0 ? { ...treatment, recurrence: value } : treatment
                                                                        ),
                                                                    }
                                                                    : condition
                                                            ),
                                                        },
                                                    }));
                                                }}
                                                className="form-select w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                                            >
                                                <option value="">{t('general.selectOption')}</option>
                                                {recurrenceOptions.map((option) => (
                                                    <option key={option.value} value={option.value}>
                                                        {getTranslatedLabel(recurrenceOptions, option.value)}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="endDate" className="block text-dafauna-darkText font-medium mb-2">{t('condition.endDate')}</label>
                                            <input
                                                type="date"
                                                id="endDate"
                                                name="endDate"
                                                value={
                                                    petData.health.conditions.find((condition) => condition.type === 'allergy')
                                                        ?.treatments[0]?.endDate || ''
                                                }
                                                onChange={(e) => {
                                                    const { value } = e.target;
                                                    setPetData((prevState) => ({
                                                        ...prevState,
                                                        health: {
                                                            ...prevState.health,
                                                            conditions: prevState.health.conditions.map((condition) =>
                                                                condition.type === 'allergy'
                                                                    ? {
                                                                        ...condition,
                                                                        treatments: condition.treatments.map((treatment, index) =>
                                                                            index === 0 ? { ...treatment, endDate: value } : treatment
                                                                        ),
                                                                    }
                                                                    : condition
                                                            ),
                                                        },
                                                    }));
                                                }}
                                                className="form-input w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                                            />
                                        </div>
                                    </>
                                )}
                            </>
                        )}
                        <div className="form-group-disease flex items-center gap-4">
                            <label htmlFor="hasDisease" className="text-dafauna-darkText font-medium">{t('condition.hasDisease')}</label>
                            <input
                                type="checkbox"
                                id="hasDisease"
                                name="hasDisease"
                                checked={petData.health?.conditions?.some((condition) => condition.type === 'disease') || false}
                                onChange={(e) => {
                                    const { checked } = e.target;
                                    setPetData((prevState) => ({
                                        ...prevState,
                                        health: {
                                            ...prevState.health,
                                            conditions: checked
                                                ? [
                                                    ...(prevState.health.conditions || []),
                                                    { type: 'disease', name: '', diagnosisDate: '', notes: '', treatments: [] },
                                                ]
                                                : prevState.health.conditions.filter((condition) => condition.type !== 'disease'),
                                        },
                                    }));
                                }}
                                className="form-checkbox"
                            />
                        </div>
                        {petData.health?.conditions?.some((condition) => condition.type === 'disease') && (
                            <>
                                <div className="form-group">
                                    <label htmlFor="diseaseName" className="block text-dafauna-darkText font-medium mb-2">{t('condition.diseaseName')}</label>
                                    <input
                                        type="text"
                                        id="diseaseName"
                                        name="diseaseName"
                                        value={
                                            petData.health.conditions.find((condition) => condition.type === 'disease')?.name || ''
                                        }
                                        onChange={(e) => {
                                            const { value } = e.target;
                                            setPetData((prevState) => ({
                                                ...prevState,
                                                health: {
                                                    ...prevState.health,
                                                    conditions: prevState.health.conditions.map((condition) =>
                                                        condition.type === 'disease' ? { ...condition, name: value } : condition
                                                    ),
                                                },
                                            }));
                                        }}
                                        className="form-input w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="diagnosisDate" className="block text-dafauna-darkText font-medium mb-2">{t('condition.diagnosisDate')}</label>
                                    <input
                                        type="date"
                                        id="diagnosisDate"
                                        name="diagnosisDate"
                                        value={
                                            petData.health.conditions.find((condition) => condition.type === 'disease')
                                                ?.diagnosisDate || ''
                                        }
                                        onChange={(e) => {
                                            const { value } = e.target;
                                            setPetData((prevState) => ({
                                                ...prevState,
                                                health: {
                                                    ...prevState.health,
                                                    conditions: prevState.health.conditions.map((condition) =>
                                                        condition.type === 'disease' ? { ...condition, diagnosisDate: value } : condition
                                                    ),
                                                },
                                            }));
                                        }}
                                        className="form-input w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="notes" className="block text-dafauna-darkText font-medium mb-2">{t('condition.notes')}</label>
                                    <textarea
                                        id="notes"
                                        name="notes"
                                        value={
                                            petData.health.conditions.find((condition) => condition.type === 'disease')?.notes || ''
                                        }
                                        onChange={(e) => {
                                            const { value } = e.target;
                                            setPetData((prevState) => ({
                                                ...prevState,
                                                health: {
                                                    ...prevState.health,
                                                    conditions: prevState.health.conditions.map((condition) =>
                                                        condition.type === 'disease' ? { ...condition, notes: value } : condition
                                                    ),
                                                },
                                            }));
                                        }}
                                        className="form-textarea w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                                    />
                                </div>
                                <div className="form-group-hasTreatment flex items-center gap-4">
                                    <label htmlFor="hasTreatment" className="text-dafauna-darkText font-medium">{t('condition.diseaseTreatment')}</label>
                                    <input
                                        type="checkbox"
                                        id="hasTreatment"
                                        name="hasTreatment"
                                        checked={
                                            petData.health.conditions.find((condition) => condition.type === 'disease')?.treatments
                                                ?.length > 0 || false
                                        }
                                        onChange={(e) => {
                                            const { checked } = e.target;
                                            setPetData((prevState) => ({
                                                ...prevState,
                                                health: {
                                                    ...prevState.health,
                                                    conditions: prevState.health.conditions.map((condition) =>
                                                        condition.type === 'disease'
                                                            ? {
                                                                ...condition,
                                                                treatments: checked
                                                                    ? [{ name: '', lastGivenDate: '', recurrence: '', endDate: '' }]
                                                                    : [],
                                                            }
                                                            : condition
                                                    ),
                                                },
                                            }));
                                        }}
                                        className="form-checkbox"
                                    />
                                </div>
                                {petData.health.conditions.find((condition) => condition.type === 'disease')?.treatments?.length > 0 && (
                                    <>
                                        <div className="form-group">
                                            <label htmlFor="treatmentName" className="block text-dafauna-darkText font-medium mb-2">{t('condition.treatmentName')}</label>
                                            <input
                                                type="text"
                                                id="treatmentName"
                                                name="treatmentName"
                                                value={
                                                    petData.health.conditions.find((condition) => condition.type === 'disease')
                                                        ?.treatments[0]?.name || ''
                                                }
                                                onChange={(e) => {
                                                    const { value } = e.target;
                                                    setPetData((prevState) => ({
                                                        ...prevState,
                                                        health: {
                                                            ...prevState.health,
                                                            conditions: prevState.health.conditions.map((condition) =>
                                                                condition.type === 'disease'
                                                                    ? {
                                                                        ...condition,
                                                                        treatments: condition.treatments.map((treatment, index) =>
                                                                            index === 0 ? { ...treatment, name: value } : treatment
                                                                        ),
                                                                    }
                                                                    : condition
                                                            ),
                                                        },
                                                    }));
                                                }}
                                                className="form-input w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="lastGivenDate" className="block text-dafauna-darkText font-medium mb-2">{t('condition.lastGivenDate')}</label>
                                            <input
                                                type="date"
                                                id="lastGivenDate"
                                                name="lastGivenDate"
                                                value={
                                                    petData.health.conditions.find((condition) => condition.type === 'disease')
                                                        ?.treatments[0]?.lastGivenDate || ''
                                                }
                                                onChange={(e) => {
                                                    const { value } = e.target;
                                                    setPetData((prevState) => ({
                                                        ...prevState,
                                                        health: {
                                                            ...prevState.health,
                                                            conditions: prevState.health.conditions.map((condition) =>
                                                                condition.type === 'disease'
                                                                    ? {
                                                                        ...condition,
                                                                        treatments: condition.treatments.map((treatment, index) =>
                                                                            index === 0 ? { ...treatment, lastGivenDate: value } : treatment
                                                                        ),
                                                                    }
                                                                    : condition
                                                            ),
                                                        },
                                                    }));
                                                }}
                                                className="form-input w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="recurrence" className="block text-dafauna-darkText font-medium mb-2">{t('condition.recurrence')}</label>
                                            <select
                                                id="recurrence"
                                                name="recurrence"
                                                value={
                                                    petData.health.conditions.find((condition) => condition.type === 'disease')
                                                        ?.treatments[0]?.recurrence || ''
                                                }
                                                onChange={(e) => {
                                                    const { value } = e.target;
                                                    setPetData((prevState) => ({
                                                        ...prevState,
                                                        health: {
                                                            ...prevState.health,
                                                            conditions: prevState.health.conditions.map((condition) =>
                                                                condition.type === 'disease'
                                                                    ? {
                                                                        ...condition,
                                                                        treatments: condition.treatments.map((treatment, index) =>
                                                                            index === 0 ? { ...treatment, recurrence: value } : treatment
                                                                        ),
                                                                    }
                                                                    : condition
                                                            ),
                                                        },
                                                    }));
                                                }}
                                                className="form-select w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                                            >
                                                <option value="">{t('general.selectOption')}</option>
                                                {recurrenceOptions.map((option) => (
                                                    <option key={option.value} value={option.value}>
                                                        {getTranslatedLabel(recurrenceOptions, option.value)}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="endDate" className="block text-dafauna-darkText font-medium mb-2">{t('condition.endDate')}</label>
                                            <input
                                                type="date"
                                                id="endDate"
                                                name="endDate"
                                                value={
                                                    petData.health.conditions.find((condition) => condition.type === 'disease')
                                                        ?.treatments[0]?.endDate || ''
                                                }
                                                onChange={(e) => {
                                                    const { value } = e.target;
                                                    setPetData((prevState) => ({
                                                        ...prevState,
                                                        health: {
                                                            ...prevState.health,
                                                            conditions: prevState.health.conditions.map((condition) =>
                                                                condition.type === 'disease'
                                                                    ? {
                                                                        ...condition,
                                                                        treatments: condition.treatments.map((treatment, index) =>
                                                                            index === 0 ? { ...treatment, endDate: value } : treatment
                                                                        ),
                                                                    }
                                                                    : condition
                                                            ),
                                                        },
                                                    }));
                                                }}
                                                className="form-input w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                                            />
                                        </div>
                                    </>
                                )}
                            </>
                        )}
                        <div className="nav-add-pet flex justify-between mt-5">
                            <button
                                onClick={() => setStep(3)}
                                className="btn back-btn bg-gray-600 text-white rounded-md px-4 py-2 hover:bg-gray-700"
                            >
                                {t('general.back')}
                            </button>
                            <button
                                onClick={handleSubmit}
                                className={`btn finish-btn bg-dafauna-primary text-white rounded-md px-4 py-2 hover:bg-dafauna-primaryLight ${
                                    loading ? 'opacity-50 cursor-not-allowed' : ''
                                }`}
                                disabled={loading}
                            >
                                {loading ? t('general.adding') : t('general.finish')}
                            </button>
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div className="add-pet-container bg-dafauna-background rounded-xl shadow-md p-8">
            <h2 className="text-dafauna-darkText text-2xl font-bold text-center mb-6">
                {t('createPetPage.titleAnyPetOrg')}
            </h2>
            {renderStep()}
        </div>
    );
};

export default CreateFirstPet;